import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import dayjs from "dayjs";
import { type FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { QuotationApprovalStats as QuotationApprovalStatsType } from "../../../../@types/services/QuotationApprovalStats";
import { getApprovalStatistics } from "../../../../__generated__/orval/endpoints/quotations/quotations";
import { ForbiddenError } from "../../../../errors/ForbiddenError";
import { useAuth } from "../../../../hooks/useAuth";
import { ValidationForm } from "./-internal_components/ValidationForm";

interface ChartData {
  date: string;
  numberQuotations: number;
  numberSelectedForApproval: number;
  selectedForApprovalRate?: number;
}

const QuotationApprovalStats: FC = () => {
  const { t } = useTranslation();

  const [rawData, setRawData] = useState<ChartData[]>([]);
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [isAccumulated, setIsAccumulated] = useState(false);
  const [isPercentage, setIsPercentage] = useState(false);
  const [startDate, setStartDate] = useState<dayjs.Dayjs>(
    dayjs().subtract(30, "day").startOf("day"),
  );
  const [endDate, setEndDate] = useState<dayjs.Dayjs>(dayjs().startOf("day"));
  const [groupBy, setGroupBy] = useState<string>("award_date");
  const [groupsFilter, setGroupsFilter] = useState<string[]>(["013", "022"]);
  const { user } = useAuth();
  const { data, isLoading, refetch } = useQuery({
    enabled: false,
    queryFn: async () => {
      const stats = await getApprovalStatistics({
        end_date: endDate.format("YYYY-MM-DD"),
        group_by: groupBy as "award_date" | "creation_date",
        groups: groupsFilter,
        start_date: startDate.format("YYYY-MM-DD"),
      });

      return stats as QuotationApprovalStatsType;
    },
    queryKey: [
      "quotationApprovalStats",
      groupBy,
      { endDate, groups: groupsFilter, startDate },
    ],
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 1,
  });

  const availableGroups = ["013", "022"];
  const groupByOptions = [
    {
      label: t("quotation.approvalStats.groupByAwardDate"),
      value: "award_date",
    },
    {
      label: t("quotation.approvalStats.groupByCreationDate"),
      value: "creation_date",
    },
  ];
  const goal = 20;

  useEffect(() => {
    if (data) {
      const allDates = Array.from(
        new Set([
          ...Object.keys(data.random_selected),
          ...Object.keys(data.not_selected),
        ]),
      ).sort();

      const formattedData = allDates.map((date) => {
        const selectedCount = data.random_selected[date] || 0;
        const notSelectedCount = data.not_selected[date] || 0;
        const totalQuotations = selectedCount + notSelectedCount;

        return {
          date,
          numberQuotations: totalQuotations,
          numberSelectedForApproval: selectedCount,
        };
      });

      setRawData(formattedData);
    }
  }, [data]);

  useEffect(() => {
    let transformedData = [...rawData];

    if (isAccumulated) {
      let accQuotations = 0;
      let accSelectedForApproval = 0;
      transformedData = transformedData.map((item) => {
        accQuotations += item.numberQuotations;
        accSelectedForApproval += item.numberSelectedForApproval;
        return {
          ...item,
          numberQuotations: accQuotations,
          numberSelectedForApproval: accSelectedForApproval,
        };
      });
    }

    if (isPercentage) {
      transformedData = transformedData.map((item) => ({
        ...item,
        selectedForApprovalRate:
          item.numberQuotations > 0
            ? (item.numberSelectedForApproval / item.numberQuotations) * 100
            : 0,
      }));
    }

    setChartData(transformedData);
  }, [isAccumulated, isPercentage, rawData]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return (
    <Container maxWidth={false} sx={{ paddingBottom: 4, paddingTop: 4 }}>
      <Typography align="left" gutterBottom sx={{ width: "100%" }} variant="h6">
        {t("quotation.approvalStats.title")}
      </Typography>
      <Paper elevation={1} sx={{ padding: 3 }}>
        <Box
          sx={{
            alignItems: "flex-end",
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <FormControl sx={{ width: "100%" }}>
            <Typography gutterBottom variant="subtitle1">
              {t("quotation.approvalStats.filters")}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  alignItems: "flex-end",
                  display: "flex",
                  flexDirection: "row",
                  gap: 2,
                  width: "100%",
                }}
              >
                <DatePicker
                  format="DD/MM/YYYY"
                  label={t("quotation.approvalStats.startDate")}
                  onChange={(newValue) => setStartDate(dayjs(newValue))}
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: { width: 220 },
                    },
                  }}
                  value={startDate}
                />
                <DatePicker
                  format="DD/MM/YYYY"
                  label={t("quotation.approvalStats.endDate")}
                  onChange={(newValue) => setEndDate(dayjs(newValue))}
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: { width: 220 },
                    },
                  }}
                  value={endDate}
                />
                <Autocomplete
                  multiple
                  onChange={(_, newValue) => setGroupsFilter(newValue)}
                  options={availableGroups}
                  renderInput={(params) => (
                    <TextField
                      fullWidth
                      key={params.slotProps.htmlInput.id}
                      label={t("quotation.approvalStats.groups")}
                      size="small"
                      slotProps={{
                        htmlInput: params.slotProps.htmlInput,
                        input: {
                          ...params.slotProps.input,
                        },
                      }}
                    />
                  )}
                  size="small"
                  value={groupsFilter}
                />
                <Autocomplete
                  onChange={(_, newValue) =>
                    setGroupBy(newValue?.value || "award_date")
                  }
                  options={groupByOptions}
                  renderInput={(params) => (
                    <TextField
                      fullWidth
                      key={params.slotProps.htmlInput.id}
                      label={t("quotation.approvalStats.groupBy")}
                      size="small"
                      slotProps={{
                        htmlInput: params.slotProps.htmlInput,
                        input: {
                          ...params.slotProps.input,
                        },
                      }}
                      sx={{ width: 220 }}
                    />
                  )}
                  size="small"
                  value={groupByOptions.find(
                    (option) => option.value === groupBy,
                  )}
                />

                <Button
                  loading={isLoading}
                  onClick={() => {
                    refetch();
                  }}
                  variant="contained"
                >
                  {t("quotation.approvalStats.update")}
                </Button>
              </Box>
            </Box>
          </FormControl>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            marginBottom: 2,
            marginTop: 3,
            paddingBottom: 2,
          }}
        >
          <FormControl>
            <Typography gutterBottom variant="subtitle1">
              {t("quotation.approvalStats.visualization")}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isAccumulated}
                    onChange={(e) => setIsAccumulated(e.target.checked)}
                  />
                }
                label={t("quotation.approvalStats.accumulated")}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isPercentage}
                    onChange={(e) => setIsPercentage(e.target.checked)}
                  />
                }
                label={t("quotation.approvalStats.percentage")}
              />
            </Box>
          </FormControl>
        </Box>

        <ResponsiveContainer height={400} width="100%">
          <LineChart
            data={chartData}
            margin={{
              bottom: 20,
              left: 20,
              right: 30,
              top: 40,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              angle={-45}
              dataKey="date"
              height={70}
              label={{ offset: 50, position: "bottom", value: "Data" }}
              textAnchor="end"
            />
            <YAxis
              label={{
                angle: -90,
                offset: 0,
                position: "insideLeft",
                value: isPercentage
                  ? t("quotation.approvalStats.percentage")
                  : t("quotation.approvalStats.quantity"),
              }}
            />

            <Tooltip
              formatter={(value, name) => {
                const numeric = Number(value);
                if (String(name).includes("%")) {
                  return [`${numeric.toFixed(1)}%`, name];
                }
                return [Math.round(numeric), name];
              }}
            />
            <Legend align="right" verticalAlign="top" />
            {!!isPercentage && [
              <ReferenceLine
                key="reference-line"
                label={t("quotation.approvalStats.goal", {
                  goal,
                })}
                stroke="#ff0000"
                strokeDasharray="3 3"
                y={20}
              />,
              <Line
                dataKey="selectedForApprovalRate"
                dot={{ r: 4 }}
                key="selectedForApprovalRate"
                name={t("quotation.approvalStats.selectedForApprovalRate")}
                stroke="#8884d8"
                strokeWidth={2}
                type="monotone"
              />,
            ]}
            {!isPercentage && [
              <Line
                dataKey="numberSelectedForApproval"
                dot={{ r: 4 }}
                key="numberSelectedForApproval"
                name={
                  isAccumulated
                    ? t(
                        "quotation.approvalStats.selectedForApprovalAccumulated",
                      )
                    : t("quotation.approvalStats.selectedForApproval")
                }
                stroke="#82ca9d"
                strokeWidth={2}
                type="monotone"
              />,
              <Line
                dataKey="numberQuotations"
                dot={{ r: 4 }}
                key="numberQuotations"
                name={
                  isAccumulated
                    ? t("quotation.approvalStats.quotationsAccumulated")
                    : t("quotation.approvalStats.quotations")
                }
                stroke="#8884d8"
                strokeWidth={2}
                type="monotone"
              />,
            ]}
          </LineChart>
        </ResponsiveContainer>
      </Paper>
      {["development", "homolog"].includes(window.environment) &&
        user?.user_groups_names.includes("Developer") && <ValidationForm />}
    </Container>
  );
};

export const Route = createFileRoute(
  "/_private/vbuyer/quotations/approval_stats",
)({
  beforeLoad({ context }) {
    if (context.auth.user?.roles_names.includes("Quotation Approval - Stats")) {
      return;
    }

    throw new ForbiddenError();
  },
  component: QuotationApprovalStats,
});
